import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArchiveRestore, CalendarCheck2, Download, HardDriveDownload, Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import api, { errMsg } from "@/lib/api";

const KIND = { auto: "Harian", manual: "Manual", "sebelum-pulih": "Sebelum pulih" };
const size = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function BackupSettingsSection() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(null);

  const load = () => api.get("/backups").then((r) => setData(r.data)).catch(() => setData({ items: [], keep: 7 }));
  useEffect(() => { load(); }, []);

  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); } };
  const backupNow = () => run("now", async () => { const r = await api.post("/backups", null, { timeout: 300000 }); toast.success(`Backup dicipta: ${r.data.name}`); load(); });
  const download = (name) => run(`dl-${name}`, async () => {
    const r = await api.get(`/backups/${name}/download`, { responseType: "blob", timeout: 300000 });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  });
  const restore = (name) => run(`rs-${name}`, async () => {
    const r = await api.post(`/backups/${name}/restore`, null, { timeout: 600000 });
    const n = Object.values(r.data.restored).reduce((a, b) => a + b, 0);
    toast.success(`Database dipulihkan (${n} rekod). Salinan keselamatan: ${r.data.safety_backup}`);
    setTimeout(() => window.location.reload(), 1500);
  });

  return (
    <section className="panel fade-up p-6 lg:col-span-2" data-testid="settings-backup-section">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary"><HardDriveDownload className="h-4 w-4" /></div>
          <div>
            <h2 className="font-bold">Backup automatik</h2>
            <p className="mt-1 text-sm text-muted-foreground">Salinan penuh database dibuat setiap hari dan disimpan dalam folder projek <code className="font-mono text-xs">backups-auto/</code>. Bila app diklon semula dan database kosong, backup terbaharu dipulihkan sendiri.</p>
          </div>
        </div>
        <Button onClick={backupNow} disabled={!!busy} data-testid="backup-now-btn">{busy === "now" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <HardDriveDownload className="mr-2 h-4 w-4" />}Backup sekarang</Button>
      </div>

      <div className="mt-5 flex flex-wrap gap-3 text-xs">
        <span className={`flex items-center gap-1.5 rounded-full px-3 py-1 ${data?.today_done ? "bg-emerald-500/10 text-emerald-300" : "bg-amber-500/10 text-amber-300"}`} data-testid="backup-today-status">
          <CalendarCheck2 className="h-3.5 w-3.5" />{data?.today_done ? "Backup hari ini sudah ada" : "Backup hari ini belum dibuat (dibuat sendiri dalam 10 minit)"}
        </span>
        <span className="rounded-full border border-border px-3 py-1 text-muted-foreground">Simpan {data?.keep ?? 7} salinan terakhir</span>
      </div>
      <p className="mt-3 flex items-start gap-2 text-xs text-amber-200/80"><ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />Fail backup mengandungi data sensitif (hash kata laluan, token Page). Pastikan repo GitHub anda private.</p>

      <div className="mt-5 divide-y divide-border rounded-lg border border-border" data-testid="backup-list">
        {!data ? <div className="p-4"><Loader2 className="h-4 w-4 animate-spin text-primary" /></div>
          : !data.items.length ? <p className="p-4 text-sm text-muted-foreground" data-testid="backup-empty">Belum ada backup.</p>
          : data.items.map((b) => (
            <div key={b.name} className="flex flex-wrap items-center justify-between gap-3 p-3" data-testid={`backup-row-${b.name}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium">{b.date} · {b.time} <span className="ml-2 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">{KIND[b.kind]}</span></p>
                <p className="break-all font-mono text-[11px] text-muted-foreground">{b.name} · {size(b.size)}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => download(b.name)} disabled={!!busy} data-testid="backup-download-btn">{busy === `dl-${b.name}` ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1.5 h-3.5 w-3.5" />}Muat turun</Button>
                <Button size="sm" variant="outline" onClick={() => setConfirm(b)} disabled={!!busy} data-testid="backup-restore-btn">{busy === `rs-${b.name}` ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <ArchiveRestore className="mr-1.5 h-3.5 w-3.5" />}Pulihkan</Button>
              </div>
            </div>
          ))}
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="border-border bg-card" data-testid="backup-restore-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Pulihkan backup {confirm?.date} {confirm?.time}?</AlertDialogTitle>
            <AlertDialogDescription>Semua data semasa (group, post, tetapan) akan DIGANTI dengan isi backup ini. Salinan keselamatan data semasa dibuat dahulu secara automatik. Anda mungkin perlu log masuk semula.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="backup-restore-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={() => { restore(confirm.name); setConfirm(null); }} data-testid="backup-restore-accept">Ya, pulihkan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
